import { doc, getDoc, setDoc, onSnapshot, collection, getDocs, addDoc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import { sendEmailNotification } from './emailService';

export interface ContactMethodConfig {
  id: 'email' | 'telegram' | 'whatsapp' | 'message';
  title: string;
  subtitle: string;
  value: string; // email address, telegram handle/link, whatsapp number
  actionUrl: string; // mailto:..., https://t.me/..., https://wa.me/...
  enabled: boolean;
  iconType: 'email' | 'telegram' | 'whatsapp' | 'message' | 'phone' | 'clock' | 'globe' | 'shield';
  badgeText: string;
  bgColor: string;
  statusText: string;
}

export interface ContactCenterSettings {
  email: ContactMethodConfig;
  telegram: ContactMethodConfig;
  whatsapp: ContactMethodConfig;
  supportBlock: ContactMethodConfig;
  businessNotificationEmail: string;
  heroTitle: string;
  heroSubtitle: string;
  operatingHoursText: string;
  customEditsHoursText: string;
  updatedAt: string;
}

export interface ContactMessageTicket {
  id: string;
  name: string;
  email: string;
  subject: string;
  category: string;
  message: string;
  status: 'unread' | 'read' | 'replied';
  createdAt: string;
  adminReply?: string;
  repliedAt?: string;
}

export const DEFAULT_CONTACT_CENTER_SETTINGS: ContactCenterSettings = {
  email: {
    id: 'email',
    title: 'Email Support',
    subtitle: 'Direct email assistance for template customization, zip downloads & order inquiries.',
    value: 'support@rheditography.net',
    actionUrl: 'mailto:support@rheditography.net',
    enabled: true,
    iconType: 'email',
    badgeText: 'Official Support Desk',
    bgColor: 'from-blue-600 to-cyan-500',
    statusText: 'Response within 1-2 hours'
  },
  telegram: {
    id: 'telegram',
    title: 'Telegram Support',
    subtitle: 'Instant 1-on-1 messaging for rapid technical help & custom vector requests.',
    value: '@rheditography',
    actionUrl: 'https://t.me/rheditography',
    enabled: true,
    iconType: 'telegram',
    badgeText: 'Instant Chat',
    bgColor: 'from-sky-500 to-blue-600',
    statusText: 'Direct Telegram Chat'
  },
  whatsapp: {
    id: 'whatsapp',
    title: 'WhatsApp Support',
    subtitle: 'Direct WhatsApp support channel for instant order verification & downloads.',
    value: '+1 (800) 555-0199',
    actionUrl: 'https://wa.me/18005550199',
    enabled: true,
    iconType: 'whatsapp',
    badgeText: 'Live Messaging',
    bgColor: 'from-emerald-500 to-teal-600',
    statusText: 'Instant WhatsApp Chat'
  },
  supportBlock: {
    id: 'message', // using message as fallback since it's generic, or I can add clock
    title: 'Support Hours',
    subtitle: 'Mon - Sat (GMT)',
    value: '24/7 Downloads',
    actionUrl: '',
    enabled: true,
    iconType: 'phone', // using phone as a generic icon or message
    badgeText: 'Active',
    bgColor: 'from-purple-500 to-fuchsia-600',
    statusText: 'Always On'
  },
  businessNotificationEmail: 'support@rheditography.net',
  heroTitle: "We're here to help you",
  heroSubtitle: 'Have questions about document templates, custom 3D vector requests, or licensing? Get in touch with our support team.',
  operatingHoursText: '24/7 Instant Downloads',
  customEditsHoursText: 'Mon - Sat (GMT)',
  updatedAt: new Date().toISOString()
};

const LOCAL_STORAGE_SETTINGS_KEY = 'rh_contact_center_settings';
const LOCAL_STORAGE_MESSAGES_KEY = 'rh_contact_messages';

// Local Storage Helpers
export function getLocalContactCenterSettings(): ContactCenterSettings {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return { ...DEFAULT_CONTACT_CENTER_SETTINGS, ...parsed };
    }
  } catch (e) {
    console.warn('Failed to load contact center settings from localStorage:', e);
  }
  return DEFAULT_CONTACT_CENTER_SETTINGS;
}

export function saveLocalContactCenterSettings(settings: ContactCenterSettings) {
  try {
    localStorage.setItem(LOCAL_STORAGE_SETTINGS_KEY, JSON.stringify(settings));
  } catch (e) {
    console.warn('Failed to save contact center settings to localStorage:', e);
  }
}

export async function fetchContactCenterSettingsFromFirestore(): Promise<ContactCenterSettings> {
  try {
    const docRef = doc(db, 'site_settings', 'contact_center');
    const snapshot = await getDoc(docRef);
    if (snapshot.exists()) {
      const data = snapshot.data() as Partial<ContactCenterSettings>;
      const merged: ContactCenterSettings = {
        ...DEFAULT_CONTACT_CENTER_SETTINGS,
        ...data,
        email: { ...DEFAULT_CONTACT_CENTER_SETTINGS.email, ...(data.email || {}) },
        telegram: { ...DEFAULT_CONTACT_CENTER_SETTINGS.telegram, ...(data.telegram || {}) },
        whatsapp: { ...DEFAULT_CONTACT_CENTER_SETTINGS.whatsapp, ...(data.whatsapp || {}) }
      };
      saveLocalContactCenterSettings(merged);
      return merged;
    }
  } catch (err) {
    console.warn('[Firestore] Contact settings fetch fallback:', err);
  }
  return getLocalContactCenterSettings();
}

export function subscribeToContactCenterSettings(callback: (settings: ContactCenterSettings) => void): () => void {
  callback(getLocalContactCenterSettings());

  fetchContactCenterSettingsFromFirestore().then((settings) => {
    if (settings) {
      callback(settings);
    }
  }).catch((err) => {
    console.warn('One-time contact settings fetch error:', err);
  });

  return () => {};
}

export async function saveContactCenterSettingsToFirestore(settings: ContactCenterSettings): Promise<void> {
  saveLocalContactCenterSettings(settings);
  try {
    const docRef = doc(db, 'site_settings', 'contact_center');
    await setDoc(docRef, {
      ...settings,
      updatedAt: new Date().toISOString()
    }, { merge: true });
  } catch (err) {
    console.error('[Firestore Error] Failed to save contact center settings:', err);
  }
}

// Contact Messages / Tickets
export function getLocalContactMessages(): ContactMessageTicket[] {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_MESSAGES_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (e) {
    console.warn('Failed to load contact messages from localStorage:', e);
  }
  return [];
}

export function saveLocalContactMessages(messages: ContactMessageTicket[]) {
  try {
    localStorage.setItem(LOCAL_STORAGE_MESSAGES_KEY, JSON.stringify(messages));
  } catch (e) {
    console.warn('Failed to save contact messages to localStorage:', e);
  }
}

export function subscribeToContactMessages(callback: (messages: ContactMessageTicket[]) => void) {
  const colRef = collection(db, 'contact_messages');
  const q = query(colRef, orderBy('createdAt', 'desc'));

  return onSnapshot(q, (snapshot) => {
    const list: ContactMessageTicket[] = [];
    snapshot.forEach((docSnap) => {
      list.push({
        id: docSnap.id,
        ...(docSnap.data() as Omit<ContactMessageTicket, 'id'>)
      });
    });
    saveLocalContactMessages(list);
    callback(list);
  }, (err) => {
    console.warn('[Firestore] Contact messages subscription fallback:', err);
    callback(getLocalContactMessages());
  });
}

export async function submitContactMessage(payload: {
  name: string;
  email: string;
  subject: string;
  category: string;
  message: string;
}): Promise<ContactMessageTicket> {
  // Simple Anti-Spam / Gibberish Detection
  const isSpamName = payload.name.length > 15 && !payload.name.includes(' ');
  const isSpamMessage = payload.message.split(' ').some(word => word.length > 40);
  
  if (isSpamName || isSpamMessage) {
    console.warn('Blocked potential spam submission');
    throw new Error('Your request was flagged as spam. Please use a valid name and message.');
  }

  const ticketId = `TICK-${Math.floor(100000 + Math.random() * 900000)}`;
  const ticket: ContactMessageTicket = {
    id: ticketId,
    name: payload.name.trim(),
    email: payload.email.trim(),
    subject: payload.subject.trim(),
    category: payload.category || 'General Support',
    message: payload.message.trim(),
    status: 'unread',
    createdAt: new Date().toISOString()
  };

  // 1. Save locally
  const currentLocal = getLocalContactMessages();
  saveLocalContactMessages([ticket, ...currentLocal]);

  // 2. Save to Firestore
  try {
    const docRef = doc(db, 'contact_messages', ticketId);
    await setDoc(docRef, ticket);
  } catch (err) {
    console.error('[Firestore Error] Failed to save contact message ticket:', err);
  }

  // 3. Send Notification Email to Business Email (support@rheditography.net)
  const contactSettings = getLocalContactCenterSettings();
  const businessEmail = contactSettings.businessNotificationEmail || 'support@rheditography.net';

  try {
    await sendEmailNotification({
      to: businessEmail,
      subject: `[CONTACT TICKET ${ticketId}] ${ticket.subject}`,
      type: 'CUSTOM',
      html: `
        <div style="font-family: Arial, sans-serif; background-color: #081225; color: #FFFFFF; padding: 24px; border-radius: 12px; border: 1px solid #1E2D4A;">
          <h2 style="color: #00D4FF; margin-top: 0;">New Contact Form Submission</h2>
          <p style="color: #B8C7E0;">A new ticket <strong>#${ticketId}</strong> has been received on RH Editography.</p>
          
          <div style="background-color: #0F1C38; padding: 16px; border-radius: 8px; margin: 16px 0; border-left: 4px solid #00D4FF;">
            <p style="margin: 0 0 8px 0;"><strong>Customer Name:</strong> ${ticket.name}</p>
            <p style="margin: 0 0 8px 0;"><strong>Email Address:</strong> ${ticket.email}</p>
            <p style="margin: 0 0 8px 0;"><strong>Category:</strong> ${ticket.category}</p>
            <p style="margin: 0 0 8px 0;"><strong>Subject:</strong> ${ticket.subject}</p>
            <p style="margin: 0; color: #E2E8F0;"><strong>Message:</strong><br />${ticket.message.replace(/\n/g, '<br />')}</p>
          </div>

          <p style="font-size: 12px; color: #64748B;">Log into Admin Panel &gt; Contact Center Inbox to respond directly to this customer.</p>
        </div>
      `
    });
  } catch (e) {
    console.warn('Business notification email dispatch warning:', e);
  }

  // 4. Send Customer Auto Reply ("We Received Your Message")
  try {
    await sendEmailNotification({
      to: ticket.email,
      subject: 'We Received Your Message',
      type: 'CUSTOM',
      html: `
        <div style="font-family: Arial, sans-serif; background-color: #081225; color: #FFFFFF; padding: 24px; border-radius: 12px; border: 1px solid #1E2D4A;">
          <h2 style="color: #22C55E; margin-top: 0;">We Received Your Message</h2>
          <p style="color: #B8C7E0; font-size: 15px; line-height: 1.6;">
            Hello <strong>${ticket.name}</strong>,
          </p>
          <p style="color: #B8C7E0; font-size: 14px; line-height: 1.6;">
            Thank you for contacting <strong>RH Editography</strong>.
          </p>
          <p style="color: #B8C7E0; font-size: 14px; line-height: 1.6;">
            Our support team has received your request (Ticket ID: <strong>#${ticketId}</strong>) and will respond as soon as possible.
          </p>
          
          <div style="background-color: #0F1C38; padding: 16px; border-radius: 8px; margin: 20px 0; border: 1px solid rgba(255,255,255,0.08);">
            <p style="margin: 0 0 6px 0; font-size: 12px; color: #00D4FF; font-weight: bold;">Ticket Details Summary:</p>
            <p style="margin: 0 0 4px 0; font-size: 13px; color: #CBD5E1;"><strong>Subject:</strong> ${ticket.subject}</p>
            <p style="margin: 0; font-size: 13px; color: #CBD5E1;"><strong>Category:</strong> ${ticket.category}</p>
          </div>

          <p style="color: #B8C7E0; font-size: 14px; margin-top: 24px;">
            Support Team<br />
            <strong>RH Editography</strong>
          </p>
        </div>
      `
    });
  } catch (e) {
    console.warn('Customer auto-reply dispatch warning:', e);
  }

  return ticket;
}

export async function updateContactMessageStatus(ticketId: string, status: 'unread' | 'read' | 'replied'): Promise<void> {
  const current = getLocalContactMessages();
  const updated = current.map(m => m.id === ticketId ? { ...m, status } : m);
  saveLocalContactMessages(updated);

  try {
    const docRef = doc(db, 'contact_messages', ticketId);
    await updateDoc(docRef, { status });
  } catch (err) {
    console.error('[Firestore Error] Failed to update contact message status:', err);
  }
}

export async function replyToContactMessage(ticketId: string, replyText: string, customerEmail: string, customerName: string, subject: string): Promise<void> {
  const now = new Date().toISOString();
  const current = getLocalContactMessages();
  const updated = current.map(m => m.id === ticketId ? { ...m, status: 'replied' as const, adminReply: replyText, repliedAt: now } : m);
  saveLocalContactMessages(updated);

  try {
    const docRef = doc(db, 'contact_messages', ticketId);
    await updateDoc(docRef, {
      status: 'replied',
      adminReply: replyText,
      repliedAt: now
    });
  } catch (err) {
    console.error('[Firestore Error] Failed to update ticket reply in Firestore:', err);
  }

  // Dispatch email to customer
  try {
    await sendEmailNotification({
      to: customerEmail,
      subject: `Re: ${subject} [RH Editography Support]`,
      type: 'CUSTOM',
      html: `
        <div style="font-family: Arial, sans-serif; background-color: #081225; color: #FFFFFF; padding: 24px; border-radius: 12px; border: 1px solid #1E2D4A;">
          <h2 style="color: #00D4FF; margin-top: 0;">RH Editography Support Team Reply</h2>
          <p style="color: #B8C7E0;">Hello <strong>${customerName}</strong>,</p>
          
          <div style="background-color: #0F1C38; padding: 18px; border-radius: 10px; margin: 16px 0; border-left: 4px solid #00D4FF;">
            <p style="margin: 0; color: #FFFFFF; font-size: 14px; line-height: 1.6; white-space: pre-line;">${replyText}</p>
          </div>

          <p style="font-size: 12px; color: #64748B; margin-top: 20px;">
            Ticket Ref: #${ticketId} | RH Editography Support Desk
          </p>
        </div>
      `
    });
  } catch (e) {
    console.warn('Reply email dispatch warning:', e);
  }
}

export async function deleteContactMessage(ticketId: string): Promise<void> {
  const current = getLocalContactMessages();
  const updated = current.filter(m => m.id !== ticketId);
  saveLocalContactMessages(updated);

  try {
    const docRef = doc(db, 'contact_messages', ticketId);
    await deleteDoc(docRef);
  } catch (err) {
    console.error('[Firestore Error] Failed to delete contact message:', err);
  }
}
